import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eggSalesService } from '../services/apiServices';
import { EggSale, PaginatedResponse } from '../types';
// ── Broken Egg Sales ──────────────────────────────────────────────────────────
import { brokenEggSalesService } from '../services/apiServices';

// Get all egg sales
export const useEggSales = (params?: any) => {
  return useQuery<PaginatedResponse<EggSale>>({
    queryKey: ['eggSales', params],
    queryFn: () => eggSalesService.getAll(params),
  });
};

// Get single egg sale
export const useEggSale = (id: number) => {
  return useQuery<EggSale>({
    queryKey: ['eggSale', id],
    queryFn: () => eggSalesService.getById(id),
    enabled: !!id,
  });
};

// Get egg sales stats
export const useEggSalesStats = (params?: any) => {
  return useQuery({
    queryKey: ['eggSalesStats', params],
    queryFn: () => eggSalesService.getStats(params),
  });
};

// Get pending sales
export const usePendingSales = () => {
  return useQuery<EggSale[]>({
    queryKey: ['pendingSales'],
    queryFn: () => eggSalesService.getPending(),
  });
};

// Create batch egg sales mutation (one customer, multiple procurement batches)
export const useCreateEggSaleBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => eggSalesService.createBatch(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eggSales'] });
      queryClient.invalidateQueries({ queryKey: ['eggSalesStats'] });
      queryClient.invalidateQueries({ queryKey: ['pendingSales'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardOverview'] });
      queryClient.invalidateQueries({ queryKey: ['depositStats'] });
    },
  });
};

// Create egg sale mutation
export const useCreateEggSale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => eggSalesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eggSales'] });
      queryClient.invalidateQueries({ queryKey: ['eggSalesStats'] });
      queryClient.invalidateQueries({ queryKey: ['pendingSales'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardOverview'] });
    },
  });
};

// Update egg sale mutation
export const useUpdateEggSale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => eggSalesService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['eggSales'] });
      queryClient.invalidateQueries({ queryKey: ['eggSale', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['eggSalesStats'] });
      queryClient.invalidateQueries({ queryKey: ['pendingSales'] });
      queryClient.invalidateQueries({ queryKey: ['depositStats'] });
    },
  });
};

// Delete egg sale mutation
export const useDeleteEggSale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => eggSalesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eggSales'] });
      queryClient.invalidateQueries({ queryKey: ['eggSalesStats'] });
      queryClient.invalidateQueries({ queryKey: ['pendingSales'] });
    },
  });
};

// Bulk mark all unpaid sales as paid
export const useBulkMarkSalesPaid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params?: any) => eggSalesService.bulkMarkPaid(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eggSales'] });
      queryClient.invalidateQueries({ queryKey: ['eggSalesStats'] });
      queryClient.invalidateQueries({ queryKey: ['pendingSales'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardOverview'] });
    },
  });
};

// Mark sale as paid mutation
export const useMarkSalePaid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => eggSalesService.markPaid(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['eggSales'] });
      queryClient.invalidateQueries({ queryKey: ['eggSale', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['eggSalesStats'] });
      queryClient.invalidateQueries({ queryKey: ['pendingSales'] });
    },
  });
};

export const useBrokenEggSales = (params?: any) =>
  useQuery({
    queryKey: ['brokenEggSales', params],
    queryFn: () => brokenEggSalesService.getAll(params),
  });

export const useBrokenEggStock = () =>
  useQuery({
    queryKey: ['brokenEggStock'],
    queryFn: brokenEggSalesService.getStock,
    staleTime: 60_000,
  });

export const useCreateBrokenEggSale = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => brokenEggSalesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brokenEggSales'] });
      queryClient.invalidateQueries({ queryKey: ['brokenEggStock'] });
    },
  });
};
